import { useDelayedStatus } from '../hooks/useDelayedStatus';
import PageHeader from './PageHeader';
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Recording, Summary, MeetingType, ProcessingStatus, Tag, ChatMessage } from '../types';
import ApiService from '../services/apiService';
import { featureFlags } from '../services/featureFlags';
import SummaryEditor from './SummaryEditor';
import AudioPlayer, { AudioPlayerRef } from './AudioPlayer';
import ProgressBar from './ProgressBar';
import TranscriptViewer from './TranscriptViewer';
import SupportingDocuments from './SupportingDocuments';
import TagsManager from './TagsManager';
import ChatOverlay from './ChatOverlay';
import FileShareModal from './FileShareModal';
import { deriveEffectiveAccess } from '../utils/sharePermissions';
import { dispatchRecordingUpdated } from '../utils/recorderEvents';
import { useAuth } from '../contexts/AuthContext';
import { downloadBlob } from '../utils/downloadFile';
import { getRecentTemplateNames, rememberRecentTemplate } from '../utils/recentTemplates';
import { summaryRefForTemplate } from '../utils/templateRef';
import '../styles/SummaryEditor.css';
import '../styles/RecordingDetail.css';
import '../styles/TemplatePicker.css';
import { logger } from '../utils/logger';

const parseDateString = (value?: string | null): Date | null => {
    if (!value) {
        return null;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateTimeValue = (value?: string | null): string | null => {
    const date = parseDateString(value);
    if (!date) {
        return null;
    }

    // Check if time is 00:00
    const isMinnight = date.getHours() === 0 && date.getMinutes() === 0;

    if (isMinnight) {
        // Only show date when time is 00:00
        return date.toLocaleDateString('nl-NL', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    }

    // Show date and time
    return date.toLocaleString('nl-NL', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const RecordingDetailPage: React.FC = () => {
    const { t } = useTranslation();
    const { recordingId } = useParams<{ recordingId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const [recording, setRecording] = useState<Recording | null>(null);
    const [summaries, setSummaries] = useState<Summary[]>([]);
    const [meetingTypes, setMeetingTypes] = useState<MeetingType[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedSummary, setSelectedSummary] = useState<Summary | null>(null);
    const summaryLoadRef = useRef<object | null>(null);
    const summaryScopeRef = useRef<object>({});
    useEffect(() => {
        summaryScopeRef.current = {};
        return () => { summaryScopeRef.current = {}; summaryLoadRef.current = null; };
    }, [recordingId]);
    const [, setIsEditing] = useState(false);
    const [transcript, setTranscript] = useState('');

    // Nieuwe state voor de uitgebreide functionaliteiten
    const [processingStatus, setProcessingStatus] = useState<ProcessingStatus>(ProcessingStatus.NOT_STARTED);
    const visibleProcessingStatus = useDelayedStatus([ProcessingStatus.PROCESSING, ProcessingStatus.QUEUED].includes(processingStatus) ? processingStatus : null);
    const [estimatedRemainingTime, setEstimatedRemainingTime] = useState(0);
    const [isCheckingStatus, setIsCheckingStatus] = useState(false);
    const [isEditingName, setIsEditingName] = useState(false);
    const [currentAudioTime, setCurrentAudioTime] = useState(0);
    const [, setAudioDuration] = useState(0);
    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [showDateTimeModal, setShowDateTimeModal] = useState(false);
    const [editDateTime, setEditDateTime] = useState('');

    const canCurrentUserShare = Boolean(user?.capabilities?.can_share ?? user?.canShare);

    const access = deriveEffectiveAccess({
        canEdit: recording?.canEdit,
        canDownload: recording?.canDownload,
        canManage: recording?.canManage,
        sharedPermission: recording?.sharedPermission ?? null,
    });

    const canEditContent = recording ? access.canEdit : false;
    const canDownloadContent = recording ? access.canDownload : false;
    const canManageShares = recording ? access.canManage : false;
    const canShowShareOption = canManageShares && canCurrentUserShare;
    const hasActions = canManageShares || canEditContent || canDownloadContent;
    const canAskQuestion = processingStatus === ProcessingStatus.PROCESSED && Boolean(transcript);
    const recordedOnText = formatDateTimeValue(recording?.recordingDateTime ?? recording?.createdAt ?? null);

    // Debug log voor showDeleteConfirm changes
    useEffect(() => {
        logger.log('showDeleteConfirm changed to:', showDeleteConfirm);
    }, [showDeleteConfirm]);

    useEffect(() => {
        if (!canEditContent) {
            setIsEditingName(false);
        }
    }, [canEditContent]);

    useEffect(() => {
        if (showShareModal && !canShowShareOption) {
            setShowShareModal(false);
        }
    }, [showShareModal, canShowShareOption]);


    const [isDeleting, setIsDeleting] = useState(false);
    const [activeTab, setActiveTab] = useState<string | null>(null);

    // Chat ("Stel een vraag") over het transcript, ook zonder open verslag
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
    const [menuOpen, setMenuOpen] = useState(false);
    const [showTemplateGrid, setShowTemplateGrid] = useState(false);
    const [templateFilter, setTemplateFilter] = useState('');

    // Nieuwe state voor additional recordings selectie
    const [allRecordings, setAllRecordings] = useState<Recording[]>([]);
    const [selectedAdditionalRecordings, setSelectedAdditionalRecordings] = useState<Recording[]>([]);
    const [recordingSearchTerm, setRecordingSearchTerm] = useState('');
    const [showRecordingDropdown, setShowRecordingDropdown] = useState(false);

    // State voor aantal ondersteunende documenten
    const [supportingDocumentCount, setSupportingDocumentCount] = useState(0);

    // Vrije notitie ("Opmerking") - opgeslagen als eerste tekst-document bij de opname
    const [noteContent, setNoteContent] = useState('');
    const [noteOriginalContent, setNoteOriginalContent] = useState('');
    const [noteHasChanges, setNoteHasChanges] = useState(false);
    const [noteIsSaving, setNoteIsSaving] = useState(false);
    const [noteDocId, setNoteDocId] = useState<number | null>(null);



    const apiService = new ApiService();
    const audioPlayerRef = useRef<AudioPlayerRef>(null);
    const recordingDropdownRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const noteTextareaRef = useRef<HTMLTextAreaElement>(null);
    const noteAutoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const noteRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isFetchingFreeNoteRef = useRef(false);
    // De notitie blijft tijdens het opslaan bewerkbaar, dus houden we buiten de
    // render-state bij of er al een opslag loopt en of er daarna nog een moet
    // volgen voor tekst die tijdens het opslaan is getypt.
    const noteSaveInFlightRef = useRef(false);
    const notePendingSaveRef = useRef(false);
    // Spiegelt de laatste notitie-state zodat timer-callbacks geen verouderde
    // closures gebruiken.
    const noteStateRef = useRef({
        content: '',
        originalContent: '',
        hasChanges: false,
        isSaving: false,
        docId: null as number | null,
        processingStatus,
    });

    useEffect(() => {
        noteStateRef.current = {
            content: noteContent,
            originalContent: noteOriginalContent,
            hasChanges: noteHasChanges,
            isSaving: noteIsSaving,
            docId: noteDocId,
            processingStatus,
        };
    }, [noteContent, noteOriginalContent, noteHasChanges, noteIsSaving, noteDocId, processingStatus]);

    // Alleen mét focus meegroeien met de inhoud; zonder focus blijft het veld
    // compact zodat het geen ruimte inneemt van de rest van de UI.
    const resizeNoteTextarea = () => {
        const el = noteTextareaRef.current;
        if (!el) return;
        if (document.activeElement !== el) {
            el.style.height = '';
            return;
        }
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
    };

    // Zet content/original/docId en reset wijzigingen, maar alleen als de waarde
    // echt verandert (voorkomt onnodige re-renders / cursorsprongen).
    const syncFreeNoteState = (content: string, docId: number | null) => {
        setNoteContent(prev => (prev !== content ? content : prev));
        setNoteOriginalContent(prev => (prev !== content ? content : prev));
        setNoteDocId(prev => (prev !== docId ? docId : prev));
        setNoteHasChanges(false);
    };

    // Lees de vrije notitie uit: zoek het eerste tekst-document en haal de inhoud op.
    const loadFreeNote = React.useCallback(async () => {
        if (isFetchingFreeNoteRef.current || !recordingId) return;
        isFetchingFreeNoteRef.current = true;
        try {
            const docs = await apiService.getSupportingDocuments(recordingId);
            const textTypes = ['txt', 'text/plain', 'text', 'text/txt'];
            const textDoc = docs.find(d => textTypes.includes((d.content_type || '').toLowerCase()));
            if (textDoc) {
                const docWithContent = await apiService.getSupportingDocumentContent(textDoc.id);
                const content = docWithContent?.content || '';
                syncFreeNoteState(content, textDoc.id);
            }
        } catch (err) {
            logger.warn('Could not load free note:', err);
        } finally {
            isFetchingFreeNoteRef.current = false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recordingId]);

    // Sla de vrije notitie op: PATCH als er al een document is, anders POST (nieuw doc).
    const handleNoteSave = React.useCallback(async () => {
        const { content, docId } = noteStateRef.current;
        if (!recordingId || (!content.trim() && !docId)) return;

        // Eén opslag tegelijk. Loopt er al een, dan gaat wat nu getypt is mee in
        // een volgende ronde in plaats van een tweede gelijktijdig verzoek.
        if (noteSaveInFlightRef.current) {
            notePendingSaveRef.current = true;
            return;
        }

        noteSaveInFlightRef.current = true;
        const savedContent = content;
        setNoteIsSaving(true);
        try {
            if (docId) {
                await apiService.updateSupportingDocumentContent(docId, savedContent);
            } else {
                const timestamp = new Date().toISOString().slice(0, 16).replace(/:/g, '-');
                const newDoc = await apiService.addSupportingDocumentContent(
                    recordingId,
                    savedContent,
                    `Handmatige-invoer-${timestamp}`
                );
                setNoteDocId(newDoc.id);
            }

            setNoteOriginalContent(savedContent);
            // De gebruiker kan tijdens het opslaan zijn doorgetypt; vergelijk met
            // de actuele inhoud in plaats van blind op "opgeslagen" te zetten.
            setNoteHasChanges(noteStateRef.current.content !== savedContent);
        } catch (err) {
            logger.error('Error saving free note:', err);
        } finally {
            noteSaveInFlightRef.current = false;
            setNoteIsSaving(false);
        }

        // Tekst die tijdens het opslaan is bijgekomen alsnog wegschrijven.
        if (notePendingSaveRef.current || noteStateRef.current.content !== savedContent) {
            notePendingSaveRef.current = false;
            scheduleNoteSave();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recordingId]);

    // Debounced autosave: pas opslaan als er 1500ms niet getypt is.
    const scheduleNoteSave = () => {
        if (noteAutoSaveTimerRef.current) {
            clearTimeout(noteAutoSaveTimerRef.current);
        }
        noteAutoSaveTimerRef.current = setTimeout(() => {
            noteAutoSaveTimerRef.current = null;
            if (noteStateRef.current.hasChanges) {
                void handleNoteSave();
            }
        }, 1500);
    };

    // Sluit het opties-menu bij klik buiten of Escape
    useEffect(() => {
        if (!menuOpen) return;
        const handlePointer = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setMenuOpen(false);
            }
        };
        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setMenuOpen(false);
        };
        document.addEventListener('mousedown', handlePointer);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handlePointer);
            document.removeEventListener('keydown', handleKey);
        };
    }, [menuOpen]);

    useEffect(() => {
        if (recordingId) {
            // Reset view-state zodat we bij het wisselen van opname (bijv. via de
            // sidebar) altijd terugvallen op de detailpagina i.p.v. een open summary
            setSelectedSummary(null);
            setIsEditing(false);
            setActiveTab(null);
            setMenuOpen(false);
            setShowTemplateGrid(false);
            setSelectedAdditionalRecordings([]);
            setShowShareModal(false);
            setShowDeleteConfirm(false);
            setShowDateTimeModal(false);
            setIsChatOpen(false);
            setChatMessages([]);

            // Reset vrije notitie en laad opnieuw voor deze opname
            setNoteContent('');
            setNoteOriginalContent('');
            setNoteHasChanges(false);
            setNoteIsSaving(false);
            setNoteDocId(null);
            void loadFreeNote();

            loadRecordingData();
            loadAllRecordings();
            // Set the audio URL
            setAudioUrl(`/audio/${recordingId}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recordingId]);

    // Laat de notitie-textarea meegroeien met de inhoud. Ook opnieuw meten
    // wanneer de textarea pas later mount (canEditContent wordt async waar).
    useEffect(() => {
        resizeNoteTextarea();
    }, [noteContent, canEditContent]);

    // Periodieke refresh van de vrije notitie zolang de verwerking loopt en er
    // nog geen document is: vangt het geval af dat de backend tijdens verwerking
    // zelf een tekstdocument aanmaakt. Stopt zodra er een docId is of je typt.
    useEffect(() => {
        const inProgress = (
            processingStatus === ProcessingStatus.UPLOADING ||
            processingStatus === ProcessingStatus.QUEUED ||
            processingStatus === ProcessingStatus.PROCESSING
        );

        const clearRefresh = () => {
            if (noteRefreshTimerRef.current) {
                clearTimeout(noteRefreshTimerRef.current);
                noteRefreshTimerRef.current = null;
            }
        };

        const shouldRefresh = () => {
            const s = noteStateRef.current;
            return inProgress && s.docId === null && !s.hasChanges && !s.isSaving;
        };

        const scheduleRefresh = () => {
            clearRefresh();
            if (!shouldRefresh()) return;
            noteRefreshTimerRef.current = setTimeout(async () => {
                await loadFreeNote();
                scheduleRefresh();
            }, 1800);
        };

        scheduleRefresh();
        return clearRefresh;
    }, [recordingId, processingStatus, noteDocId, noteHasChanges, noteIsSaving, loadFreeNote]);

    // Ruim de autosave-timer op bij unmount.
    useEffect(() => () => {
        if (noteAutoSaveTimerRef.current) {
            clearTimeout(noteAutoSaveTimerRef.current);
        }
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            // Check if click is inside recording dropdown
            if (showRecordingDropdown && recordingDropdownRef.current) {
                const isInsideDropdown = recordingDropdownRef.current.contains(event.target as Node);

                if (isInsideDropdown) {
                    return; // Don't close dropdown if click is inside
                }
            }

            if (showRecordingDropdown) {
                setShowRecordingDropdown(false);
            }
        };

        if (showRecordingDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showRecordingDropdown]);



    // Reset active tab als de feature wordt uitgeschakeld
    useEffect(() => {
        if (!canEditContent && (activeTab === 'documents' || activeTab === 'additional-recordings')) {
            setActiveTab(null);
            return;
        }

        if (activeTab === 'documents' && !featureFlags.isEnabled('supportingDocuments')) {
            setActiveTab(null);
        }
        if (activeTab === 'additional-recordings' && !featureFlags.isEnabled('additionalRecordings')) {
            setActiveTab(null);
        }
        // Note: tagsManager heeft geen eigen tab, dus geen reset nodig
    }, [activeTab, canEditContent]);



    const initialStateUsedRef = useRef(false);

    const loadRecordingData = async () => {
        if (!recordingId) return;

        try {
            setIsLoading(true);

            // Gebruik navigatie state alleen bij de eerste keer laden
            const stateRecording = !initialStateUsedRef.current
                ? location.state?.recording as Recording | undefined
                : undefined;
            initialStateUsedRef.current = true;

            const [recordingData, summariesData, meetingTypesData, transcriptData, supportingDocsData] = await Promise.all([
                // Gebruik de meegegeven recording data of haal het op via API
                stateRecording || apiService.getFile(recordingId),
                apiService.getSummaries(recordingId),
                apiService.getMeetingTypes(),
                apiService.getTranscript(recordingId),
                // Haal de supporting documents op voor de count
                apiService.getSupportingDocuments(recordingId)
            ]);

            // Recording data van getFile() bevat al de tags
            const recordingWithTags = recordingData;

            setRecording(recordingWithTags);
            setSummaries(summariesData);
            setMeetingTypes(meetingTypesData);
            setTranscript(transcriptData);
            setSupportingDocumentCount(supportingDocsData.length);

            if (recordingData) {
                setProcessingStatus(recordingData.status);
            }

            // Start status checking als de opname nog wordt verwerkt
            if (recordingData?.status === ProcessingStatus.PROCESSING ||
                recordingData?.status === ProcessingStatus.QUEUED ||
                recordingData?.status === ProcessingStatus.UPLOADING) {
                checkStatus();
            }
        } catch (err) {
            setError(t('recordingDetail.errors.loadRecording'));
            logger.error('Error loading recording data:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const loadAllRecordings = async () => {
        try {
            const recordings = await apiService.getRecordings();
            // Filter out the current recording
            const filteredRecordings = recordings.filter(r => r.id !== recordingId);
            setAllRecordings(filteredRecordings);
        } catch (err) {
            logger.error('Error loading all recordings:', err);
        }
    };

    const handleCreateSummary = async (template: MeetingType) => {
        if (!recordingId || !canEditContent || summaryLoadRef.current) return;

        // Nieuw verslag met de uuid van het gekozen template; de naam blijft de
        // identiteit van het verslag in de UI (lijst, drafts, recente templates)
        const { meetingType, templateUuid } = summaryRefForTemplate(template);
        rememberRecentTemplate(meetingType);

        // Maak een lege samenvatting aan en ga direct naar de SummaryEditor
        // De streaming zal daar automatisch starten
        const newSummary: Summary = {
            id: `${recordingId}-${meetingType}`,
            fileId: recordingId,
            meetingType,
            templateUuid,
            content: '', // Start met lege content
            sourceContent: '',
            title: recording?.name || t('recordingDetail.summaryDefaultTitle'),
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };

        // Ga direct naar de SummaryEditor - streaming zal daar worden gestart
        setSelectedSummary(newSummary);
    };

    const handleViewSummary = async (summary: Summary) => {
        if (!recordingId || summaryLoadRef.current) return;
        const request = {};
        summaryLoadRef.current = request;
        const scope = summaryScopeRef.current;
        setError('');

        try {
            const loadedSummary = await apiService.getSummary(recordingId, summary);
            if (summaryScopeRef.current !== scope) return;
            if (loadedSummary) {
                // Preserve original meetingType if the API response didn't include it
                if (!loadedSummary.meetingType) {
                    loadedSummary.meetingType = summary.meetingType;
                }
                setSelectedSummary({ ...summary, ...loadedSummary });
            } else {
                setError(t('recordingDetail.errors.loadSummary'));
            }
        } catch (err) {
            logger.error('Error loading summary:', err);
            if (summaryScopeRef.current === scope) setError(t('recordingDetail.errors.loadSummary'));
        } finally {
            if (summaryLoadRef.current === request) summaryLoadRef.current = null;
        }
    };

    const handleSummaryUpdate = (updatedSummary: Summary) => {
        setSummaries(prev => prev.some(s => s.fileId === updatedSummary.fileId && s.meetingType === updatedSummary.meetingType)
            ? prev.map(s => s.fileId === updatedSummary.fileId && s.meetingType === updatedSummary.meetingType ? updatedSummary : s)
            : [...prev, updatedSummary]);
        setSelectedSummary(updatedSummary);
    };

    const handleBackFromSummary = () => {
        setSelectedSummary(null);
        if (!recordingId) return;
        const scope = summaryScopeRef.current;
        void apiService.getSummaries(recordingId).then(items => {
            if (summaryScopeRef.current === scope) setSummaries(items);
        }).catch(err => {
            if (summaryScopeRef.current === scope) setError(t('recordingDetail.errors.loadSummary'));
            logger.error('Error refreshing summaries:', err);
        });
    };

    const handleSummaryDelete = (deletedSummary: Summary) => {
        setSummaries(prev =>
            prev.filter(s => s.meetingType !== deletedSummary.meetingType)
        );
    };

    // Nieuwe functies voor status checking en file processing
    const checkStatus = async () => {
        if (!recordingId || isCheckingStatus) return;

        setIsCheckingStatus(true);

        const checkStatusRecursively = async () => {
            try {
                const statusData = await apiService.getRecordingStatus(recordingId);
                setProcessingStatus(statusData.status);

                if (statusData.audioDuration) {
                    setAudioDuration(statusData.audioDuration);
                }

                if (statusData.startTime && statusData.estimatedProcessingTime) {
                    const now = Date.now() / 1000;
                    const elapsed = now - statusData.startTime;
                    const remaining = Math.max(0, statusData.estimatedProcessingTime - elapsed);
                    setEstimatedRemainingTime(remaining);
                }

                // Continue checking als nog steeds aan het verwerken
                if (statusData.status === ProcessingStatus.PROCESSING ||
                    statusData.status === ProcessingStatus.QUEUED ||
                    statusData.status === ProcessingStatus.UPLOADING) {
                    setTimeout(checkStatusRecursively, 8000); // Check elke 8 seconden
                } else if (statusData.status === ProcessingStatus.PROCESSED) {
                    // Herlaad data als verwerking klaar is
                    await loadRecordingData();
                }
            } catch (error) {
                logger.error('Error checking status:', error);
            }
        };

        await checkStatusRecursively();
        setIsCheckingStatus(false);
    };

    const handleProcessFile = async () => {
        if (!recordingId || !canEditContent) return;

        try {
            await apiService.processFile(recordingId);
            checkStatus();
        } catch (error) {
            setError(t('recordingDetail.errors.processFile'));
            logger.error('Error processing file:', error);
        }
    };

    const handleRenameFile = async (newName: string) => {
        if (!recordingId || !recording || !canEditContent) return;

        try {
            const success = await apiService.renameRecording(recordingId, newName);
            if (success) {
                setRecording({ ...recording, name: newName });
                setIsEditingName(false);
                dispatchRecordingUpdated(recordingId, newName);
            } else {
                setError(t('recordingDetail.errors.renameFile'));
            }
        } catch (error) {
            setError(t('recordingDetail.errors.renameFile'));
            logger.error('Error renaming file:', error);
        }
    };

    const handleDownloadAudio = async () => {
        if (!recordingId || !recording || !canDownloadContent) return;

        try {
            const audioBlob = await apiService.getAudio(recordingId);
            await downloadBlob(audioBlob, `${recording.name}.mp3`);
        } catch (error) {
            setError(t('recordingDetail.errors.downloadAudio'));
            logger.error('Error downloading audio:', error);
        }
    };

    const handleDownloadTranscript = async () => {
        if (!transcript || !recording || !canDownloadContent) return;

        const blob = new Blob([transcript], { type: 'text/plain' });
        await downloadBlob(blob, `transcript_${recording.name}.txt`);
    };

    const handleDeleteRecording = async () => {
        if (!recordingId || !recording || isDeleting || !canManageShares) return;

        setIsDeleting(true);

        try {
            await apiService.deleteRecording(recordingId);

            // Navigeer terug naar overzicht met een melding
            navigate('/recordings', {
                state: {
                    message: t('recordingDetail.deleteSuccess', { name: recording.name }),
                    messageType: 'success'
                }
            });
        } catch (error) {
            setError(t('recordingDetail.errors.deleteRecording'));
            logger.error('Error deleting recording:', error);
            setIsDeleting(false);
        }
    };

    const handleTagsChange = (tags: Tag[]) => {
        if (recording && canEditContent) {
            setRecording({ ...recording, tags });
        }
    };

    const handleDocumentCountChange = (count: number) => {
        setSupportingDocumentCount(count);
    };

    const handleDateTimeClick = () => {
        if (!canEditContent || !recording) return;

        // Format current datetime for datetime-local input
        const currentDateTime = recording.recordingDateTime ?? recording.createdAt;
        if (currentDateTime) {
            const date = new Date(currentDateTime);
            // Format as YYYY-MM-DDTHH:mm for datetime-local input
            const formatted = new Date(date.getTime() - (date.getTimezoneOffset() * 60000))
                .toISOString()
                .slice(0, 16);
            setEditDateTime(formatted);
        }
        setShowDateTimeModal(true);
    };

    const handleSaveDatetime = async () => {
        if (!recordingId || !editDateTime) return;

        try {
            await apiService.updateRecordingDatetime(recordingId, editDateTime);

            // Refresh recording data
            await loadRecordingData();
            dispatchRecordingUpdated(recordingId);

            // Close modal
            setShowDateTimeModal(false);
            setEditDateTime('');
        } catch (err) {
            logger.error('Error updating recording datetime:', err);
            setError(t('recordingDetail.errors.updateDatetime'));
        }
    };

    const MAX_ADDITIONAL_RECORDINGS = 3;

    const handleAddAdditionalRecording = (recordingToAdd: Recording) => {
        logger.log('🎯 handleAddAdditionalRecording called with:', recordingToAdd.name);
        logger.log('📋 Current selectedAdditionalRecordings:', selectedAdditionalRecordings.map(r => r.name));

        // Check if maximum limit is reached
        if (!canEditContent) {
            return;
        }

        if (selectedAdditionalRecordings.length >= MAX_ADDITIONAL_RECORDINGS) {
            logger.log('🚫 Maximum number of additional recordings reached');
            return;
        }

        if (!selectedAdditionalRecordings.find(r => r.id === recordingToAdd.id)) {
            logger.log('✅ Recording not found in list, adding...');
            setSelectedAdditionalRecordings(prev => {
                const newList = [...prev, recordingToAdd];
                return newList;
            });
        } else {
            logger.log('❌ Recording already exists in list');
        }
        setRecordingSearchTerm('');
        setShowRecordingDropdown(false);
    };

    const handleRemoveAdditionalRecording = (recordingId: string) => {
        if (!canEditContent) {
            return;
        }

        setSelectedAdditionalRecordings(prev => prev.filter(r => r.id !== recordingId));
    };

    const getFilteredRecordings = () => {
        // Als er geen zoekterm is, toon de meest recente opnames (eerste 10)
        if (!recordingSearchTerm.trim()) {
            return [...allRecordings]
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                .slice(0, 10);
        }

        const searchTerm = recordingSearchTerm.toLowerCase();
        return allRecordings.filter(recording =>
            recording.name.toLowerCase().includes(searchTerm) ||
            recording.tags?.some(tag => tag.name.toLowerCase().includes(searchTerm))
        );
    };

    const getMeetingTypeTitle = (meetingTypeName: string) => {
        // Probeer eerst te matchen op name
        let meetingType = meetingTypes.find(mt => mt.name === meetingTypeName);

        // Als dat niet werkt, probeer dan op title
        if (!meetingType) {
            meetingType = meetingTypes.find(mt => mt.title === meetingTypeName);
        }

        return meetingType?.title || meetingTypeName;
    };

    const getAvailableMeetingTypes = () => {
        const usedTypes = summaries.map(s => s.meetingType);
        return meetingTypes.filter(mt =>
            !usedTypes.includes(mt.name) && !usedTypes.includes(mt.title)
        );
    };

    // Groepeert beschikbare templates voor de kiezer: laatst gebruikt → eigen →
    // organisatie → standaard, alfabetisch per groep, gefilterd op de zoekterm.
    // Laatst-gebruikt is een sneltoegang bovenop de volledige groepen en
    // verdwijnt zodra er gezocht wordt.
    const getGroupedMeetingTypes = () => {
        const query = templateFilter.trim().toLowerCase();
        const available = getAvailableMeetingTypes();
        const matches = available.filter(mt =>
            !query ||
            mt.title.toLowerCase().includes(query) ||
            (mt.description || '').toLowerCase().includes(query)
        );
        const byTitle = (a: MeetingType, b: MeetingType) =>
            a.title.localeCompare(b.title, 'nl', { sensitivity: 'base' });
        const recent = query ? [] : getRecentTemplateNames()
            .map(name => available.find(mt => mt.name === name))
            .filter((mt): mt is MeetingType => Boolean(mt))
            .slice(0, 3);
        return [
            { key: 'recent' as const, items: recent },
            { key: 'personal' as const, items: matches.filter(mt => !mt.is_default && !mt.is_org_wide).sort(byTitle) },
            { key: 'org' as const, items: matches.filter(mt => mt.is_org_wide && !mt.is_default).sort(byTitle) },
            { key: 'standard' as const, items: matches.filter(mt => mt.is_default).sort(byTitle) },
        ].filter(group => group.items.length > 0);
    };

    if (isLoading) {
        return (
            <div className="summary-editor recording-detail">
                <div className="detail-loading">
                    <span className="spinner-small" />
                    <span>{t('recordingDetail.loading')}</span>
                </div>
            </div>
        );
    }

    if (!recording) {
        return (
            <div className="summary-editor recording-detail">
                <div className="empty-state" style={{ paddingTop: '20vh' }}>
                    <h2 style={{ color: 'var(--text-strong)', fontSize: '1.05rem', margin: '0 0 12px' }}>{t('recordingDetail.notFound.title')}</h2>
                    <button className="action-button primary" onClick={() => navigate('/recordings')}>
                        {t('recordingDetail.notFound.backToOverview')}
                    </button>
                </div>
            </div>
        );
    }

    if (selectedSummary) {
        return (
            <SummaryEditor
                key={`${recordingId}:${selectedSummary.fileId}:${selectedSummary.meetingType}`}
                summary={selectedSummary}
                recording={recording}
                meetingTypeTitle={getMeetingTypeTitle(selectedSummary.meetingType)}
                onBack={handleBackFromSummary}
                onBackToRecordings={() => navigate('/recordings')}
                onUpdate={handleSummaryUpdate}
                onDelete={handleSummaryDelete}
                onGenerationReload={(serverSummaries, serverRecording, title) => {
                    if (serverSummaries) setSummaries(serverSummaries);
                    setRecording(current => current ? { ...current, ...serverRecording, name: title } : current);
                    if (recordingId) dispatchRecordingUpdated(recordingId, title);
                }}
                onRecordingTitleUpdate={async (newTitle: string) => {
                    if (!recordingId || !recording) return;
                    try {
                        const success = await apiService.renameRecording(recordingId, newTitle);
                        if (success) {
                            setRecording({ ...recording, name: newTitle });
                            dispatchRecordingUpdated(recordingId, newTitle);
                        }
                    } catch (error) {
                        logger.error('Error auto-renaming recording:', error);
                    }
                }}
                additionalRecordings={selectedAdditionalRecordings}
                canEdit={canEditContent}
                canDownload={canDownloadContent}
            />
        );
    }

    return (
        <div className={`summary-editor recording-detail ${isChatOpen ? 'summary-editor--chat-open' : ''}`}>
            <PageHeader title={recording.name} onBack={() => navigate('/recordings')}
                breadcrumbs={[{ label: t('recordingsPage.title'), onClick: () => navigate('/recordings') }]}
                titleContent={<>
                    {isEditingName && canEditContent ? (
                        <input
                            className="editor-title editor-title-input"
                            value={recording.name}
                            autoFocus
                            onChange={(e) => setRecording({ ...recording, name: e.target.value })}
                            onBlur={() => handleRenameFile(recording.name)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') { handleRenameFile(recording.name); }
                                else if (e.key === 'Escape') { setIsEditingName(false); }
                            }}
                        />
                    ) : (
                        <h1
                            className={`editor-title ${canEditContent ? 'editor-title-editable' : ''}`}
                            onDoubleClick={() => canEditContent && setIsEditingName(true)}
                            title={canEditContent ? t('recordingDetail.renameHint') : undefined}
                        >
                            {recording.name}
                        </h1>
                    )}
                </>}
                actions={<>
                {(hasActions || canAskQuestion) && (
                    <div className="detail-header-actions">
                        {canAskQuestion && (
                            <button
                                type="button"
                                className={`detail-ask-button ${isChatOpen ? 'is-active' : ''}`}
                                onClick={() => setIsChatOpen(prev => !prev)}
                                aria-pressed={isChatOpen}
                                title={t('recordingDetail.tabs.askQuestionTitle')}
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 7.5c-2 0-3.5 1.3-3.5 3" />
                                    <path d="M12 14.5h0" />
                                    <path d="M5 19v-6a7 7 0 117 7H5z" />
                                </svg>
                                <span className="detail-ask-button-label">{t('recordingDetail.tabs.askQuestion')}</span>
                            </button>
                        )}
                        {hasActions && (
                        <div className="detail-header-menu" ref={menuRef}>
                            <button
                                type="button"
                                className={`detail-menu-toggle ${menuOpen ? 'is-active' : ''}`}
                                onClick={() => setMenuOpen((v) => !v)}
                                title={t('recordingDetail.menu.more')}
                            >
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <circle cx="12" cy="5" r="2" />
                                    <circle cx="12" cy="12" r="2" />
                                    <circle cx="12" cy="19" r="2" />
                                </svg>
                            </button>
                            {menuOpen && (
                                <div className="detail-menu-popover">
                                    {canEditContent && (
                                        <button type="button" className="detail-menu-item" onClick={() => { setMenuOpen(false); setIsEditingName(true); }}>
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M12 20h9" />
                                                <path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4z" />
                                            </svg>
                                            {t('recordingDetail.menu.rename')}
                                        </button>
                                    )}
                                    {canShowShareOption && (
                                        <button type="button" className="detail-menu-item" onClick={() => { setMenuOpen(false); setShowShareModal(true); }}>
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="18" cy="5" r="3" />
                                                <circle cx="6" cy="12" r="3" />
                                                <circle cx="18" cy="19" r="3" />
                                                <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
                                            </svg>
                                            {t('recordingDetail.menu.share')}
                                        </button>
                                    )}
                                    {canDownloadContent && (
                                        <button type="button" className="detail-menu-item" onClick={() => { setMenuOpen(false); handleDownloadAudio(); }}>
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M12 3v12" />
                                                <path d="M8 11l4 4 4-4" />
                                                <path d="M5 21h14" />
                                            </svg>
                                            {t('recordingDetail.menu.downloadAudio')}
                                        </button>
                                    )}
                                    {canDownloadContent && transcript && (
                                        <button type="button" className="detail-menu-item" onClick={() => { setMenuOpen(false); handleDownloadTranscript(); }}>
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                <path d="M14 2v6h6" />
                                                <path d="M12 18v-6" />
                                                <path d="M9 15l3 3 3-3" />
                                            </svg>
                                            {t('recordingDetail.menu.downloadTranscript')}
                                        </button>
                                    )}
                                    {canManageShares && (
                                        <button type="button" className="detail-menu-item is-danger" onClick={() => { setMenuOpen(false); setShowDeleteConfirm(true); }}>
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="3 6 5 6 21 6" />
                                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                                <path d="M10 11v6M14 11v6" />
                                                <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                                            </svg>
                                            {t('recordingDetail.menu.delete')}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                        )}
                    </div>
                )}

                </>}
                secondary={<>
                <div className="recording-meta-row">
                    <button
                        type="button"
                        className="recording-date-chip"
                        onClick={canEditContent ? handleDateTimeClick : undefined}
                        title={canEditContent ? t('recordingDetail.editDateHint') : undefined}
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                            <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {t('recordingDetail.recordedOn', { moment: recordedOnText ?? t('recordingDetail.unknownMoment') })}
                    </button>
                    {recording?.duration && (
                        <span className="recording-duration-label">{t('recordingDetail.duration', { duration: recording.duration })}</span>
                    )}
                    {recording.id && featureFlags.isEnabled('tagsManager') && canEditContent && (
                        <TagsManager
                            fileId={recording.id}
                            currentTags={recording.tags || []}
                            onTagsChange={handleTagsChange}
                            className="recording-tags-inline"
                        />
                    )}
                </div>
                </>}
            />

            {error && (
                <div className="notice error">
                    <p>{error}</p>
                    <button type="button" onClick={() => setError('')}>{t('common.close')}</button>
                </div>
            )}

            <div className="recording-detail-body">
                <div className="detail-player-card">
                    <div className="detail-player-audio">
                        <AudioPlayer
                            ref={audioPlayerRef}
                            fileId={recordingId!}
                            audioUrl={audioUrl || undefined}
                            onAudioLoaded={setAudioDuration}
                            onTimeUpdate={setCurrentAudioTime}
                        />
                    </div>

                    {visibleProcessingStatus === ProcessingStatus.PROCESSING && (
                        <div className="player-status-strip">
                            <span className="player-status-strip-dot is-animated" />
                            <div className="player-status-strip-main">
                                <span className="player-status-strip-title">{t('recordingDetail.status.processing.title')}</span>
                                <span className="player-status-strip-copy">{t('recordingDetail.status.processing.copy')}</span>
                                <div style={{ marginTop: 8 }}>
                                    <ProgressBar estimatedRemainingTime={estimatedRemainingTime} isProcessing={true} />
                                </div>
                            </div>
                        </div>
                    )}
                    {visibleProcessingStatus === ProcessingStatus.QUEUED && (
                        <div className="player-status-strip">
                            <span className="player-status-strip-dot is-animated" />
                            <div className="player-status-strip-main">
                                <span className="player-status-strip-title">{t('recordingDetail.status.queued.title')}</span>
                                <span className="player-status-strip-copy">{t('recordingDetail.status.queued.copy')}</span>
                            </div>
                        </div>
                    )}
                    {processingStatus === ProcessingStatus.UPLOADING && (
                        <div className="player-status-strip">
                            <span className="player-status-strip-dot is-animated" />
                            <div className="player-status-strip-main">
                                <span className="player-status-strip-title">{t('recordingDetail.status.uploading.title')}</span>
                                <span className="player-status-strip-copy">{t('recordingDetail.status.uploading.copy')}</span>
                            </div>
                        </div>
                    )}
                    {processingStatus === ProcessingStatus.NOT_STARTED && canEditContent && (
                        <div className="player-status-strip">
                            <div className="player-status-strip-main">
                                <span className="player-status-strip-title">{t('recordingDetail.status.readyToProcess.title')}</span>
                                <span className="player-status-strip-copy">{t('recordingDetail.status.readyToProcess.copy')}</span>
                            </div>
                            <button type="button" className="action-button primary" onClick={handleProcessFile}>{t('recordingDetail.status.readyToProcess.action')}</button>
                        </div>
                    )}
                    {processingStatus === ProcessingStatus.NOT_STARTED && !canEditContent && (
                        <div className="player-status-strip">
                            <div className="player-status-strip-main">
                                <span className="player-status-strip-title">{t('recordingDetail.status.waitingForProcessing.title')}</span>
                                <span className="player-status-strip-copy">{t('recordingDetail.status.waitingForProcessing.copy')}</span>
                            </div>
                        </div>
                    )}
                    {(processingStatus === ProcessingStatus.ERROR || processingStatus === ProcessingStatus.NOT_PROCESSED) && (
                        <div className="player-status-strip is-error">
                            <svg viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <div className="player-status-strip-main">
                                <span className="player-status-strip-title">{t('recordingDetail.status.error.title')}</span>
                                <span className="player-status-strip-copy">{t('recordingDetail.status.error.copy')}</span>
                            </div>
                        </div>
                    )}

                    {processingStatus === ProcessingStatus.PROCESSED && (transcript || (featureFlags.isEnabled('supportingDocuments') && canEditContent) || (featureFlags.isEnabled('additionalRecordings') && canEditContent)) && (
                        <div className="player-tabbar">
                            {transcript && (
                                <button
                                    type="button"
                                    className={`player-tab ${activeTab === 'transcript' ? 'active' : ''}`}
                                    aria-expanded={activeTab === 'transcript'}
                                    aria-controls="recording-player-panels"
                                    onClick={() => setActiveTab(activeTab === 'transcript' ? null : 'transcript')}
                                >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    {t('recordingDetail.tabs.transcript')}
                                </button>
                            )}
                            {featureFlags.isEnabled('supportingDocuments') && canEditContent && (
                                <button
                                    type="button"
                                    className={`player-tab ${activeTab === 'documents' ? 'active' : ''}`}
                                    aria-expanded={activeTab === 'documents'}
                                    aria-controls="recording-player-panels"
                                    onClick={() => setActiveTab(activeTab === 'documents' ? null : 'documents')}
                                >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    {t('recordingDetail.tabs.documents')}
                                    {supportingDocumentCount > 0 && <span className="player-tab-badge">{supportingDocumentCount}</span>}
                                </button>
                            )}
                            {featureFlags.isEnabled('additionalRecordings') && canEditContent && (
                                <button
                                    type="button"
                                    className={`player-tab ${activeTab === 'additional-recordings' ? 'active' : ''}`}
                                    aria-expanded={activeTab === 'additional-recordings'}
                                    aria-controls="recording-player-panels"
                                    onClick={() => setActiveTab(activeTab === 'additional-recordings' ? null : 'additional-recordings')}
                                >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                                    </svg>
                                    {t('recordingDetail.tabs.additionalRecordings')}
                                    {selectedAdditionalRecordings.length > 0 && <span className="player-tab-badge">{selectedAdditionalRecordings.length}</span>}
                                </button>
                            )}
                        </div>
                    )}
                    <div className="detail-player-panels" id="recording-player-panels">
                {/* Tab content panels */}
                {activeTab === 'transcript' && transcript && (
                    <div className="player-tab-content is-transcript-expanded">
                        <TranscriptViewer
                            transcript={transcript}
                            fileId={recordingId!}
                            currentTime={currentAudioTime}
                            canDownload={canDownloadContent}
                            onSeek={(time) => {
                                if (audioPlayerRef.current) {
                                    audioPlayerRef.current.seekTo(time);
                                }
                            }}
                        />
                    </div>
                )}

                {activeTab === 'documents' && canEditContent && processingStatus === ProcessingStatus.PROCESSED && featureFlags.isEnabled('supportingDocuments') && (
                    <div className="player-tab-content">
                        <SupportingDocuments
                            fileId={recordingId!}
                            onDocumentCountChange={handleDocumentCountChange}
                        />
                    </div>
                )}

                {activeTab === 'additional-recordings' && canEditContent && featureFlags.isEnabled('additionalRecordings') && (
                    <div className="player-tab-content">
                        <div className="p-6">
                                        <div className="mb-4">
                                            <h2 className="recording-section-title">{t('recordingDetail.additionalRecordings.selectTitle')}</h2>
                                            <p className="text-sm text-gray-600 mb-4">
                                                {t('recordingDetail.additionalRecordings.selectDescription')}
                                            </p>

                                            {/* Search input */}
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    placeholder={t('recordingDetail.additionalRecordings.searchPlaceholder')}
                                                    value={recordingSearchTerm}
                                                    onChange={(e) => {
                                                        logger.log('🔍 Search input changed:', e.target.value);
                                                        setRecordingSearchTerm(e.target.value);
                                                        // Toon dropdown altijd wanneer het veld actief is
                                                        setShowRecordingDropdown(true);
                                                    }}
                                                    onFocus={() => {
                                                        logger.log('🔍 Search input focused, showing dropdown with suggestions');
                                                        setShowRecordingDropdown(true);
                                                    }}
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                                />
                                                <svg className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                </svg>

                                                {/* Dropdown with search results */}
                                                {showRecordingDropdown && getFilteredRecordings().length > 0 && (
                                                    <div
                                                        ref={recordingDropdownRef}
                                                        className="relative w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto"
                                                        onClick={(e) => {
                                                            logger.log('🔴 Dropdown container clicked');
                                                            e.stopPropagation();
                                                        }}
                                                    >
                                                        {!recordingSearchTerm.trim() && (
                                                            <div className="px-4 py-2 text-xs text-gray-500 bg-gray-50 border-b border-gray-200">
                                                                {t('recordingDetail.additionalRecordings.recentLabel')}
                                                            </div>
                                                        )}
                                                        {getFilteredRecordings().slice(0, 10).map((recording) => {
                                                            const isAlreadySelected = selectedAdditionalRecordings.find(r => r.id === recording.id) !== undefined;
                                                            const isMaxReached = selectedAdditionalRecordings.length >= MAX_ADDITIONAL_RECORDINGS;
                                                            const shouldDisable = isAlreadySelected || (isMaxReached && !isAlreadySelected);
                                                            return (
                                                                <button
                                                                    key={recording.id}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        handleAddAdditionalRecording(recording);
                                                                    }}

                                                                    disabled={shouldDisable}
                                                                    title={
                                                                        isAlreadySelected
                                                                            ? t('recordingDetail.additionalRecordings.alreadySelected')
                                                                            : isMaxReached
                                                                                ? t('recordingDetail.additionalRecordings.maxReachedTooltip')
                                                                                : undefined
                                                                    }
                                                                    className="w-full px-3 py-2 text-left hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed border-b border-gray-100 last:border-b-0 transition-colors"
                                                                    style={{ position: 'relative', zIndex: 20 }}
                                                                >
                                                                    <div className="flex items-center justify-between">
                                                                        <div className="flex-1 min-w-0">
                                                                            <div className="font-medium text-gray-900 text-sm truncate">{recording.name}</div>
                                                                            <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                                                                                {recording.duration && (
                                                                                    <>
                                                                                        <span>{recording.duration}</span>
                                                                                        <span>•</span>
                                                                                    </>
                                                                                )}
                                                                                <span>{new Date(recording.createdAt).toLocaleDateString('nl-NL')}</span>
                                                                                {recording.tags && recording.tags.length > 0 && (
                                                                                    <>
                                                                                        <span>•</span>
                                                                                        <div className="flex items-center gap-1">
                                                                                            {recording.tags.slice(0, 2).map((tag) => (
                                                                                                <span
                                                                                                    key={tag.id}
                                                                                                    className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-700"
                                                                                                >
                                                                                                    {tag.name}
                                                                                                </span>
                                                                                            ))}
                                                                                            {recording.tags.length > 2 && (
                                                                                                <span className="text-xs text-gray-400">+{recording.tags.length - 2}</span>
                                                                                            )}
                                                                                        </div>
                                                                                    </>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                        {isAlreadySelected && (
                                                                            <svg className="w-4 h-4 text-green-500 ml-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                                            </svg>
                                                                        )}
                                                                    </div>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Separator */}
                                        <div className="border-t border-gray-200 my-6"></div>

                                        {/* Selected recordings - always show this section */}
                                        <div>
                                            <div className="recording-section-header mb-3">
                                                <h2 className="recording-section-title">
                                                    {t('recordingDetail.additionalRecordings.selectedTitle', { count: selectedAdditionalRecordings.length, max: MAX_ADDITIONAL_RECORDINGS })}
                                                </h2>
                                                {selectedAdditionalRecordings.length >= MAX_ADDITIONAL_RECORDINGS && (
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                                        {t('recordingDetail.additionalRecordings.maxReachedBadge')}
                                                    </span>
                                                )}
                                            </div>

                                            {selectedAdditionalRecordings.length > 0 ? (
                                                <div className="space-y-2">
                                                    {selectedAdditionalRecordings.map((recording) => (
                                                        <div
                                                            key={recording.id}
                                                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                                                        >
                                                            <div className="flex-1">
                                                                <div className="font-medium text-gray-900">{recording.name}</div>
                                                                <div className="text-sm text-gray-500 flex items-center">
                                                                    {recording.duration && (
                                                                        <>
                                                                            <span>{recording.duration}</span>
                                                                            <span className="mx-2">•</span>
                                                                        </>
                                                                    )}
                                                                    <span>{new Date(recording.createdAt).toLocaleDateString('nl-NL')}</span>
                                                                </div>
                                                                {recording.tags && recording.tags.length > 0 && (
                                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                                        {recording.tags.slice(0, 3).map((tag) => (
                                                                            <span
                                                                                key={tag.id}
                                                                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-800"
                                                                            >
                                                                                {tag.name}
                                                                            </span>
                                                                        ))}
                                                                        {recording.tags.length > 3 && (
                                                                            <span className="text-xs text-gray-500">+{recording.tags.length - 3}</span>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <button
                                                                onClick={() => handleRemoveAdditionalRecording(recording.id)}
                                                                className="ml-3 p-1 text-red-600 hover:text-red-800"
                                                            >
                                                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                                                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                                                    </svg>
                                                    <h3 className="mt-2 text-sm font-medium text-gray-900">{t('recordingDetail.additionalRecordings.noneSelectedTitle')}</h3>
                                                    <p className="mt-1 text-sm text-gray-500">
                                                        {t('recordingDetail.additionalRecordings.noneSelectedCopy')}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                        </div>
                    </div>
                )}

                    </div>
                </div>

                {/* Vrije notitie - onder de tab-content panels */}
                {canEditContent && featureFlags.isEnabled('supportingDocuments') && (
                    <section className="recording-detail-section free-note-section" aria-labelledby="recording-note-title">
                        <div className="recording-section-header">
                            <div className="free-note-title-group">
                                <h2 id="recording-note-title" className="recording-section-title">{t('recordingDetail.freeNote.title')}</h2>
                                <span
                                    className="free-note-info"
                                    data-tooltip={t('recordingDetail.freeNote.infoTooltip')}
                                    aria-label={t('recordingDetail.freeNote.infoTooltip')}
                                    tabIndex={0}
                                >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <circle cx="12" cy="12" r="10" />
                                        <line x1="12" y1="16" x2="12" y2="12" />
                                        <line x1="12" y1="8" x2="12.01" y2="8" />
                                    </svg>
                                </span>
                            </div>
                            {noteIsSaving && (
                                <span className="free-note-status saving">
                                    <span className="spinner-small" aria-hidden="true" />
                                </span>
                            )}
                        </div>
                        <textarea
                            ref={noteTextareaRef}
                            value={noteContent}
                            onChange={(e) => {
                                const v = e.target.value;
                                setNoteContent(v);
                                setNoteHasChanges(v !== noteOriginalContent);
                                scheduleNoteSave();
                                resizeNoteTextarea();
                            }}
                            onFocus={resizeNoteTextarea}
                            onBlur={resizeNoteTextarea}
                            placeholder={t('recordingDetail.freeNote.placeholder')}
                            rows={2}
                            className="free-note-textarea"
                            aria-labelledby="recording-note-title"
                        />
                    </section>
                )}

                {processingStatus === ProcessingStatus.PROCESSED && (
                    <section className="recording-detail-section recording-summaries-section" aria-labelledby="recording-summaries-title">
                        <div className="recording-section-header">
                            <h2 id="recording-summaries-title" className="recording-summaries-title">{t('recordingDetail.summaries.sectionTitle')}<span className="recording-summaries-count">{summaries.length}</span></h2>
                        </div>

                        {summaries.length > 0 && (
                            <ul className="summaries-list">
                                {summaries.map((summary) => (
                                    <li className="summary-item" key={summary.id}>
                                        <button
                                            type="button"
                                            className="summary-open-button"
                                            onClick={() => handleViewSummary(summary)}
                                        >
                                            <span className="summary-leading-icon">
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                            </span>
                                            <div className="summary-info">
                                                <h3 className="summary-title">{getMeetingTypeTitle(summary.meetingType)}</h3>
                                                <p className="summary-meta">
                                                    {t('recordingDetail.summaries.lastUpdated', { date: new Date(summary.updatedAt).toLocaleDateString('nl-NL') })}
                                                    {summary.additionalRecordings && summary.additionalRecordings.length > 0 && (
                                                        <span className="summary-badge">{t('recordingDetail.summaries.recordingsBadge', { count: summary.additionalRecordings.length + 1 })}</span>
                                                    )}
                                                </p>
                                            </div>
                                            <svg className="chevron-icon" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                            </svg>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {canEditContent && getAvailableMeetingTypes().length > 0 && (
                            <div className="new-summary-section">
                                <button type="button" className="create-summary-toggle" onClick={() => { setShowTemplateGrid((v) => !v); setTemplateFilter(''); }}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M12 5v14M5 12h14" />
                                    </svg>
                                    {t('recordingDetail.summaries.createNew')}
                                    <svg className={`toggle-chevron ${showTemplateGrid ? 'rotated' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                {showTemplateGrid && (() => {
                                    const groups = getGroupedMeetingTypes();
                                    const showGroupLabels = groups.length > 1;
                                    return (
                                        <div className="meeting-types-picker">
                                            {getAvailableMeetingTypes().length > 7 && (
                                                <div className="meeting-type-search">
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                        <circle cx="11" cy="11" r="8" />
                                                        <path d="M21 21l-4.35-4.35" />
                                                    </svg>
                                                    <input
                                                        type="text"
                                                        value={templateFilter}
                                                        onChange={(e) => setTemplateFilter(e.target.value)}
                                                        placeholder={t('recordingDetail.summaries.searchTemplates')}
                                                        autoFocus
                                                    />
                                                </div>
                                            )}
                                            {groups.map((group) => (
                                                <div key={group.key} className="meeting-type-group">
                                                    {showGroupLabels && (
                                                        <h4 className="meeting-type-group-label">{t(`recordingDetail.summaries.groups.${group.key}`)}</h4>
                                                    )}
                                                    <div className="meeting-types-list">
                                                        {group.items.map((meetingType) => (
                                                            <button
                                                                type="button"
                                                                key={meetingType.id}
                                                                className="meeting-type-button"
                                                                onClick={() => handleCreateSummary(meetingType)}
                                                            >
                                                                <span className="meeting-type-title">{meetingType.title}</span>
                                                                {meetingType.description && <span className="meeting-type-desc">{meetingType.description}</span>}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                            {groups.length === 0 && (
                                                <p className="meeting-types-no-results">{t('recordingDetail.summaries.noTemplatesFound')}</p>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        )}

                        {summaries.length === 0 && getAvailableMeetingTypes().length === 0 && (
                            <div className="empty-state">
                                <svg className="empty-state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <p>{t('recordingDetail.summaries.emptyState')}</p>
                            </div>
                        )}
                    </section>
                )}
            </div>

            <ChatOverlay
                isOpen={isChatOpen}
                onClose={() => setIsChatOpen(false)}
                fileId={recordingId!}
                meetingType={summaries[0]?.meetingType || ''}
                templateUuid={summaries[0]?.templateUuid}
                messages={chatMessages}
                onMessagesChange={setChatMessages}
            />

            {canShowShareOption && (
                <FileShareModal
                    fileId={recordingId || ''}
                    fileName={recording?.name}
                    isOpen={showShareModal}
                    onClose={() => setShowShareModal(false)}
                />
            )}

            {/* Delete Confirmation Dialog */}
            {showDeleteConfirm && (
                <div className="modal-overlay">
                    <div className="modal-dialog">
                        <div className="modal-header">
                            <svg className="modal-icon danger" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            <h3>{t('recordingDetail.deleteModal.title')}</h3>
                        </div>
                        <p className="modal-message">
                            <Trans
                                i18nKey="recordingDetail.deleteModal.confirmText"
                                values={{ name: recording?.name }}
                                components={{ strong: <strong /> }}
                            />
                        </p>
                        <div className="modal-actions">
                            <button type="button" className="action-button ghost" onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting}>
                                {t('common.cancel')}
                            </button>
                            <button type="button" className="action-button danger" onClick={handleDeleteRecording} disabled={isDeleting}>
                                {isDeleting ? t('common.deleting') : t('common.delete')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* DateTime Edit Modal */}
            {showDateTimeModal && (
                <div className="modal-overlay">
                    <div className="modal-dialog">
                        <div className="modal-header">
                            <h3>{t('recordingDetail.datetimeModal.title')}</h3>
                        </div>
                        <p className="modal-message">
                            {t('recordingDetail.datetimeModal.description')}
                        </p>
                        <div style={{ marginBottom: 20 }}>
                            <label htmlFor="datetime-input" style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
                                {t('recordingDetail.datetimeModal.label')}
                            </label>
                            <input
                                id="datetime-input"
                                type="datetime-local"
                                value={editDateTime}
                                onChange={(e) => setEditDateTime(e.target.value)}
                                style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--primary-200)', borderRadius: 8, background: 'var(--bg-paper)', color: 'var(--text)' }}
                            />
                        </div>
                        <div className="modal-actions">
                            <button type="button" className="action-button ghost" onClick={() => { setShowDateTimeModal(false); setEditDateTime(''); }}>
                                {t('common.cancel')}
                            </button>
                            <button type="button" className="action-button primary" onClick={handleSaveDatetime}>
                                {t('common.save')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RecordingDetailPage;
